import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowsDownUp, MagnifyingGlass } from '@phosphor-icons/react';
import { api } from '../../lib/api-client';
import type { SharedScene } from '@spine-workspace/shared';

type SortKey = 'shared' | 'updated' | 'name';

export default function SharedWithMe() {
  const navigate = useNavigate();
  const [scenes, setScenes] = useState<SharedScene[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('shared');

  const load = useCallback(async () => {
    try {
      const res = await api.get<{ scenes: SharedScene[] }>('/api/scenes/shared-with-me');
      setScenes(res.scenes);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = scenes
    .filter((s) => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name);
      if (sortKey === 'updated') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      return new Date(b.sharedAt).getTime() - new Date(a.sharedAt).getTime();
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-text-tertiary font-sans text-sm">
        Loading…
      </div>
    );
  }

  if (scenes.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="font-serif text-xl font-medium text-text-secondary mb-2">Nothing shared with you yet</p>
        <p className="font-sans text-sm text-text-tertiary">
          When someone shares a scene with you, it will appear here.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Search + Sort */}
      <div className="flex items-center gap-2 mb-5">
        <div className="relative flex-1">
          <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search scenes…"
            className="w-full bg-bg-input border border-border text-text-primary rounded-md pl-8 pr-3 py-[7px] text-sm font-sans placeholder:text-text-tertiary hover:bg-bg-hover focus:outline-none focus:border-accent transition-all duration-normal"
          />
        </div>
        <div className="flex items-center gap-1">
          <ArrowsDownUp size={13} className="text-text-tertiary" />
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="bg-bg-input border border-border text-text-secondary rounded-md px-2 py-[7px] text-xs font-sans focus:outline-none focus:border-accent transition-all duration-normal"
          >
            <option value="shared">Date shared</option>
            <option value="updated">Last modified</option>
            <option value="name">Name</option>
          </select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="font-sans text-sm text-text-tertiary">No scenes match "{searchQuery}"</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((scene) => (
            <SharedSceneCard key={scene.id} scene={scene} onOpen={(id) => navigate(`/scene/${id}/edit`)} />
          ))}
        </div>
      )}
    </div>
  );
}

function SharedSceneCard({ scene, onOpen }: { scene: SharedScene; onOpen: (id: string) => void }) {
  const sharedDate = new Date(scene.sharedAt).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });

  const permColor = scene.myPermission === 'edit'
    ? 'bg-accent-muted text-accent border-accent/30'
    : 'bg-bg-elevated text-text-secondary border-border';

  return (
    <div
      className="bg-bg-surface border border-border-subtle rounded-xl overflow-hidden group cursor-pointer hover:border-accent/30 hover:shadow-md transition-all duration-normal"
      onClick={() => onOpen(scene.id)}
    >
      {/* Thumbnail */}
      <div className="aspect-video bg-bg-canvas canvas-dot-grid flex items-center justify-center relative">
        {scene.thumbnailUrl ? (
          <img src={scene.thumbnailUrl} alt={scene.name} className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-text-tertiary">
            <svg className="w-10 h-10 opacity-20" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="font-sans text-xs">No preview</span>
          </div>
        )}

        {/* Open overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-normal flex items-center justify-center">
          <button
            onClick={(e) => { e.stopPropagation(); onOpen(scene.id); }}
            className="bg-accent text-white font-sans font-medium text-xs px-3 py-1.5 rounded-md hover:bg-accent-hover hover:shadow-glow transition-all duration-normal"
          >
            Open
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="p-3 border-t border-border-subtle">
        <div className="flex items-start justify-between gap-1 mb-1">
          <p className="font-sans text-sm font-medium text-text-primary truncate">{scene.name}</p>
          <span className={`shrink-0 font-sans text-[10px] font-medium px-1.5 py-0.5 rounded border capitalize ${permColor}`}>
            {scene.myPermission}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="font-sans text-xs text-text-tertiary truncate">
            by {scene.ownerName}
          </span>
          <span className="font-sans text-xs text-text-tertiary shrink-0 ml-2">
            {sharedDate}
          </span>
        </div>
      </div>
    </div>
  );
}
